import React, { useRef, useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { Building2, Phone, FileText, Download, Edit, X, UploadCloud } from 'lucide-react';
import Button from '../../Components/Ui/Button';
import useFileUpload from '../../hooks/fileUpload';
import {
  useGetBrandByUserIdQuery,
  useUpdateBrandProfileMutation,
} from '../../app/services/brandApi';
import { useGetUserInfoQuery } from '../../app/services/authApi';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
const BrandProfile = () => {
  const userId = useSelector((state) => state.userInfo?.user?._id);
  const gstFileInputRef = useRef(null);
  const fileInputRef = useRef(null);
  const [fileError, setFileError] = useState(null);

  const {
    data: brandResponse,
    error: brandError,
    isLoading: brandLoading,
  } = useGetBrandByUserIdQuery(userId, { skip: !userId });

  const {
    data: userInfoData,
    error: userError,
    isLoading: userLoading,
    refetch: refetchUserInfo,
  } = useGetUserInfoQuery(userId, { skip: !userId });

  const brandState = {
    ...brandResponse?.data,
    loading: brandLoading,
    error: brandError ? brandError.data?.message || brandError.error : null,
  };

  const userState = {
    ...userInfoData?.data,
    loading: userLoading,
    error: userError ? userError.data?.message || userError.error : null,
  };

  const { uploadFile, uploading } = useFileUpload();
  const [logoPreview, setLogoPreview] = useState(brandState.brandLogo);
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState({});

  const [updateBrandProfile, { isLoading: isUpdating }] = useUpdateBrandProfileMutation();

  useEffect(() => {
    if (!isEditing) {
      setLogoPreview(brandResponse?.data?.brandLogo);
      setFormData({
        name: brandResponse?.data?.name || '',
        tradeName: brandResponse?.data?.tradeName || '',
        pocName: brandResponse?.data?.pocName || '',
        pocMobile: brandResponse?.data?.pocMobile || '',
        brandGst: brandResponse?.data?.brandGst || '',
        gstCertificate: brandResponse?.data?.gstCertificate?.[0] || '',
      });
    }
  }, [
    brandResponse?.data?.brandLogo,
    brandResponse?.data?.name,
    brandResponse?.data?.tradeName,
    brandResponse?.data?.pocName,
    brandResponse?.data?.pocMobile,
    brandResponse?.data?.brandGst, // Added this dependency
    userInfoData?.data?.gstNumber,
    brandResponse?.data?.gstCertificate,
    isEditing,
  ]);

  // Debug logs
  // useEffect(() => {

  // }, [brandResponse, userInfoData]);

  const handleDownloadCertificate = () => {
    if (formData.gstCertificate) {
      window.open(formData.gstCertificate, '_blank');
    }
  };

  const handleLogoChange = async (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 50 * 1024 * 1024) {
        toast.error('Logo file size must be less than 50MB.', {
          position: 'top-right',
          autoClose: 3000,
        });
        setFileError('Logo file size exceeds 50MB');
        return;
      }
      setFileError(null); // clear error if valid
      try {
        const uploadedUrl = await uploadFile(file);
        setLogoPreview(uploadedUrl);
      } catch (err) {
        toast.error('Failed to upload logo. Please try again.');
        console.error(err);
      }
    }
  };

  const handleGstCertificateChange = async (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 50 * 1024 * 1024) {
        toast.error('GST certificate size must be less than 50MB.', {
          position: 'top-right',
          autoClose: 3000,
        });
        setFileError('GST certificate exceeds 50MB');
        return;
      }
      setFileError(null);
      try {
        const uploadedUrl = await uploadFile(file);
        setFormData((prev) => ({ ...prev, gstCertificate: uploadedUrl }));
      } catch (err) {
        toast.error('Failed to upload GST certificate. Please try again.');
        console.error(err);
      }
    }
  };

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    try {
      if (!brandState._id) return alert('Brand ID missing, cannot update');

      const body = {
        brandName: formData.name,
        tradeName: formData.tradeName,
        pocName: formData.pocName,
        pocMobile: formData.pocMobile,
        brandLogo: logoPreview,
        brandGst: formData.brandGst,
        gstCertificate: formData.gstCertificate,
      };

      await updateBrandProfile({ id: brandState._id, body }).unwrap();
      await refetchUserInfo();
      setIsEditing(false);
    } catch (err) {
      console.error('Update failed:', err);
      alert('Failed to update brand profile.');
    }
  };

  const handleCancel = () => {
    setFormData({
      brandName: brandState.name || '',
      tradeName: brandState.tradeName || '',
      pocName: brandState.pocName || '',
      pocMobile: brandState.pocMobile || '',
      brandGst: brandState.brandGst || '',
      gstCertificate: brandState.gstCertificate?.[0] || '',
    });

    setIsEditing(false);
  };

  if (brandState.loading || userState.loading) {
    return (
      <div className="bg-white rounded-lg shadow-lg border border-gray-200 p-8 text-center">
        <p className="text-gray-600">Loading brand information...</p>
      </div>
    );
  }

  if (brandState.error || userState.error) {
    return (
      <div className="bg-white rounded-lg shadow-lg border border-gray-200 p-8 text-center">
        <p className="text-red-600">Error loading brand information</p>
        <p className="text-gray-600 text-sm mt-2">{brandState.error || userState.error}</p>
      </div>
    );
  }

  if (!brandState.name && !brandState.tradeName) {
    return (
      <div className="bg-white rounded-lg shadow-lg border border-gray-200 p-8 text-center">
        <p className="text-gray-600">No brand data available.</p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-lg border border-gray-200 p-8 relative">
      <div className="absolute top-2 right-8">
        {!isEditing ? (
          <Edit
            className="h-5 w-5 text-gray-600 cursor-pointer hover:text-primary"
            onClick={() => setIsEditing(true)}
          />
        ) : (
          <X
            className="h-5 w-5 text-gray-600 cursor-pointer hover:text-red-500"
            onClick={handleCancel}
          />
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        <div className="space-y-6">
          <div className="secondary-background p-6 rounded-lg border border-color">
            <div className="flex items-center mb-4">
              <Building2 className="h-5 w-5 text-primary mr-2" />
              <h3 className="text-lg font-semibold text-gray-800">Brand Information</h3>
            </div>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Brand Name</label>
                {isEditing ? (
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => handleChange('name', e.target.value)}
                    className="w-full p-2 border rounded"
                  />
                ) : (
                  <div className="text-gray-900 font-medium">{brandState.name || 'N/A'}</div>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Trade Name</label>
                {isEditing ? (
                  <input
                    type="text"
                    value={formData.tradeName}
                    onChange={(e) => handleChange('tradeName', e.target.value)}
                    className="w-full p-2 border rounded"
                  />
                ) : (
                  <div className="text-gray-900">{brandState.tradeName || 'N/A'}</div>
                )}
              </div>
            </div>
          </div>

          <div className="secondary-background p-6 rounded-lg border border-color">
            <div className="flex items-center mb-4">
              <FileText className="h-5 w-5 text-primary mr-2" />
              <h3 className="text-lg font-semibold text-gray-800">GST Information</h3>
            </div>
            <div className="space-y-5">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1 pl-3">
                  GST Number
                </label>
                {isEditing ? (
                  <input
                    type="text"
                    value={formData.brandGst}
                    onChange={(e) => handleChange('brandGst', e.target.value)}
                    className="w-full p-2 border rounded bg-white"
                  />
                ) : (
                  <div className="text-gray-900 font-mono text-lg bg-white pl-3 mb-1 p-2 rounded border">
                    {formData.brandGst || 'N/A'}
                  </div>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  GST Registration Certificate
                </label>
                <div className="flex items-center space-x-3 bg-white p-2 rounded border">
                  <FileText className="h-5 w-5 text-gray-400" />
                  <span className="text-gray-900 flex-1 truncate">
                    {typeof formData.gstCertificate === 'string' &&
                    formData.gstCertificate.trim() !== ''
                      ? formData.gstCertificate.split('/').pop() || 'Certificate Available'
                      : 'No certificate available'}
                  </span>

                  {isEditing ? (
                    <>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => gstFileInputRef.current.click()}
                        className="flex items-center space-x-1"
                        disabled={uploading}
                      >
                        <UploadCloud className="h-4 w-4" />
                        <span>{uploading ? 'Uploading...' : 'Upload Certificate'}</span>
                      </Button>
                      <input
                        type="file"
                        accept="application/pdf,image/*"
                        ref={gstFileInputRef}
                        onChange={handleGstCertificateChange}
                        className="hidden"
                        disabled={uploading}
                      />
                    </>
                  ) : (
                    formData.gstCertificate && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={handleDownloadCertificate}
                        className="flex items-center space-x-1"
                      >
                        <Download className="h-4 w-4" />
                        <span>Download</span>
                      </Button>
                    )
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="space-y-6">
          <div className="secondary-background p-5 rounded-lg border border-color">
            <div className="flex items-center mb-4">
              <Phone className="h-5 w-5 text-primary mr-2" />
              <h3 className="text-lg font-semibold text-gray-800">Contact Details</h3>
            </div>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">POC Name</label>
                {isEditing ? (
                  <input
                    type="text"
                    value={formData.pocName}
                    onChange={(e) => handleChange('pocName', e.target.value)}
                    className="w-full p-2 border rounded"
                  />
                ) : (
                  <div className="text-gray-900 font-medium">{brandState.pocName || 'N/A'}</div>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Mobile Number
                </label>
                {isEditing ? (
                  <input
                    type="text"
                    value={formData.pocMobile}
                    onChange={(e) => handleChange('pocMobile', e.target.value)}
                    className="w-full p-2 border rounded"
                  />
                ) : (
                  <div className="flex items-center space-x-2">
                    <Phone className="h-4 w-4 text-gray-400" />
                    <span className="text-gray-900 font-mono">{brandState.pocMobile || 'N/A'}</span>
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="secondary-background p-6 rounded-lg border border-color">
            <div className="flex items-center mb-2">
              <Building2 className="h-5 w-5 text-primary mr-2" />
              <h3 className="text-lg font-semibold text-gray-800">Brand Logo</h3>
            </div>
            <div className="flex flex-col items-center space-y-2">
              <img
                src={
                  logoPreview || brandState.brandLogo || 'https://via.placeholder.com/150?text=Logo'
                }
                alt={brandState.name || 'Brand Logo'}
                className="h-32 w-32 rounded-xl object-cover border-2 border-white shadow-md"
              />
              <Button
                variant="outline"
                size="sm"
                onClick={() => fileInputRef.current.click()}
                className="flex items-center space-x-1"
                disabled={uploading}
              >
                <UploadCloud className="h-4 w-4" />
                <span>{uploading ? 'Uploading...' : 'Upload Logo'}</span>
              </Button>
              <input
                type="file"
                accept="image/*"
                ref={fileInputRef}
                onChange={handleLogoChange}
                className="hidden"
                disabled={uploading}
              />
            </div>
          </div>

          {brandState.sellers && brandState.sellers.length > 0 && (
            <div className="secondary-background p-6 rounded-lg border border-color">
              <div className="flex items-center mb-4">
                <Building2 className="h-5 w-5 text-primary mr-2" />
                <h3 className="text-lg font-semibold text-gray-800">Sellers</h3>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-primary">{brandState.sellers.length}</div>
                <div className="text-sm text-gray-600">
                  {brandState.sellers.length === 1 ? 'Seller' : 'Sellers'} associated
                </div>
              </div>
            </div>
          )}
        </div>
        <div className="flex-1 secondary-background p-6 rounded-lg border border-color">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center">
              <Building2 className="h-5 w-5 text-primary mr-2" />
              <h3 className="text-lg font-semibold text-gray-800">Login Details</h3>
            </div>
          </div>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-primary mb-1">Email ID</label>
              <div className="text-gray-900 font-medium">{userInfoData?.data?.email || 'N/A'}</div>
            </div>

            <div>
              <label className="block text-sm font-medium text-primary mb-1">Phone Number</label>
              <div className="text-gray-900 font-medium">{userInfoData?.data?.phone || 'N/A'}</div>
            </div>
          </div>
          <div className="flex justify-end">
            <Button
              variant="primary"
              size="sm"
              onClick={() => window.location.replace('/change-password')}
              className="flex items-center justify-end space-x-1"
            >
              <span>Change Password</span>
            </Button>
          </div>
        </div>
      </div>

      {isEditing && (
        <div className="flex justify-end space-x-3 mt-6">
          <Button variant="outline" onClick={handleCancel}>
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            className="primary-background white-text"
            disabled={isUpdating || fileError}
          >
            {isUpdating ? 'Saving...' : 'Save'}
          </Button>
        </div>
      )}
    </div>
  );
};

export default BrandProfile;
